import React from "react";
import _ from "lodash";

import classNames from "classnames";
import { compose, graphql } from "lib/apolloClient";

import { ChevronUpOutlined } from "@toddle-edu/ds-icons";
import { onEnterOrSpaceKeyDown } from "Utils";
import withLoader from "UIComponents/GqlWithLoader";
import { getPollOptionUsersQuery } from "MessagingV2/modules/MessagingQuery";
import {
  getDefaultNameSortPreference,
  getOrganizationId,
} from "modules/CommonSelectors";

import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar";
import {
  unifiedToNative,
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { Tooltip } from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { useI18n } from "Hooks";

import classes from "./PollUsersList.scss";
import { connect } from "react-redux";

const SUB_TEXT_LENGTH = 45;

const Accordion = ({ emoji, label, count, isOpen, onToggle }) => {
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const handleKeyDown = e => {
    onEnterOrSpaceKeyDown(e, onToggle);
  };

  const unified = _.get(emoji, "unified");

  const { t } = useI18n(["common"]);

  const handleLabelRef = React.useCallback(element => {
    if (element) {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, []);

  return (
    <div
      className={classes.accordion}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={label}
    >
      <div className={classes.accordionTitle}>
        {unified ? (
          <div className={classes.emojiContainer}>
            {unifiedToNative(unified)}
          </div>
        ) : null}
        {label ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={label} />}
            condition={isOverflowing}
            type={"wrapperAsElement"}
          >
            <div
              ref={handleLabelRef}
              className={classNames("text-body", classes.textEllipsis)}
            >
              {label}
            </div>
          </ConditionalWrapper>
        ) : null}
        <div className={classes.circularDivider} />
        <div className={classNames(classes.pollVoteCount, "text-body")}>
          {t("count_vote", {
            count,
          })}
        </div>
      </div>

      <div
        className={classNames(classes.accordionIcon, {
          [classes.accordionClose]: !isOpen,
        })}
      >
        <ChevronUpOutlined />
      </div>
    </div>
  );
};

const getInitAccordionState = options =>
  _.reduce(
    options,
    (acc, option) => {
      acc[option.id] = true;
      return acc;
    },
    {}
  );

const PollUsersList = props => {
  const { options, defaultStudentNameSortPreference } = props;

  const { t } = useI18n(["common"]);

  const [accordionState, setAccordionState] = React.useState(() =>
    getInitAccordionState(options)
  );

  const handleToggle = id => {
    setAccordionState(prevState => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  return _.map(options, option => {
    const { selectedByUsers, id } = option;

    const isOpen = _.get(accordionState, id, false);

    const onToggle = () => handleToggle(id);

    return (
      <div key={id} className={classes.pollOption}>
        <Accordion
          label={option.label}
          emoji={option.metadata}
          count={_.size(selectedByUsers)}
          isOpen={isOpen}
          onToggle={onToggle}
        />
        <div className={classes.pollOptionDivider} />
        {isOpen ? (
          <div className={classes.pollOptionUserList}>
            {_.map(selectedByUsers, user => {
              const { id: avatarId, profileImage } = user;

              const name = getFormattedNameForUserType({
                user,
                orderBy: defaultStudentNameSortPreference,
              });

              const subText = getSubText({
                messagedUserObj: user,
                messagedUserRole: _.get(user, "type"),
                t,
                defaultStudentNameSortPreference,
              });

              return (
                <div className={classes.userTypeCardContainer}>
                  <MessagingAvatar
                    id={avatarId}
                    name={name}
                    url={profileImage}
                  />
                  <div className={classes.userInfoContainer}>
                    <div className="text-body">{name}</div>
                    {subText ? (
                      <ConditionalWrapper
                        wrapper={<Tooltip tooltip={subText} />}
                        condition={_.size(subText) > SUB_TEXT_LENGTH}
                        type={"wrapperAsElement"}
                      >
                        <div
                          className={classNames(
                            "text-body-s",
                            classes.userSubTitle
                          )}
                        >
                          {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
                        </div>
                      </ConditionalWrapper>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  });
};

const mapStateToProps = state => {
  return {
    defaultStudentNameSortPreference: getDefaultNameSortPreference(state),
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getPollOptionUsersQuery, {
    name: "getPollOptionUsers",
    options: ({ pollId }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: pollId,
          organizationId: getOrganizationId(),
        },
      };
    },
    props({ getPollOptionUsers }) {
      const node = _.get(getPollOptionUsers, "node", {});

      const options = _.get(node, "options", []);

      return {
        isLoading: _.includes([1, 2], getPollOptionUsers["networkStatus"]),
        isData: !_.isEmpty(node),
        options,
      };
    },
  }),
  withLoader
)(PollUsersList);
